import { useContext, useMemo } from "react";
import { AuthContext } from "@/hooks/authContext";

/**
 * UTC-everywhere refactor — Phase 4. Every wire timestamp is a
 * `DateTimeOffset` (UTC), so displaying one means converting it through a
 * named IANA zone rather than through whatever zone the browser happens to
 * be in. The pure conversion helpers live in the shared design-system
 * formatter module (single source of truth across staff + portal); this
 * module re-exports them and adds the STAFF React hooks that bind `tz` from
 * the staff auth context.
 *
 * ⚠️ THIS IS THE STAFF MODULE, AND THE ZONE IT SUPPLIES IS THE STAFF ONE.
 * The header used to say the signed-in user's `MeDto.timeZone` was "the only
 * correct way", which is true of staff and unachievable in the portal:
 * `PortalUser` carries no timezone column and `PortalMeDto` no field, so a
 * portal surface has no preference to consult and renders on
 * `FALLBACK_TIMEZONE` by design. Portal code must not import this module —
 * `portal/isolation.test.ts` forbids it — and there is no portal twin.
 */
export {
  FALLBACK_TIMEZONE,
  formatDateTimeInTz,
  formatDateInTz,
  formatTimeInTz,
  formatRelativeTime,
  formatCalendarDate,
  calendarDateInputToIso,
  isoDateInTz,
  calendarDaysUntil,
  // Same reasoning as the predicates below: its home is
  // `@/design-system/lib/format` and portal code must import it from there.
  operationalToday,
  // The three calendar-comparison predicates. Their home is
  // `@/design-system/lib/format` — PORTAL code must import them from there,
  // because this barrel is not on the portal's allow-list — but staff code
  // reaches every other date helper through here, and leaving these three out
  // makes each new staff consumer write a split import for no reason.
  isPastCalendarDay,
  isWithinCalendarDays,
  hasCalendarDayArrived,
} from "@/design-system/lib/format";

import {
  FALLBACK_TIMEZONE,
  formatDateTimeInTz,
  formatDateInTz,
  formatTimeInTz,
  formatRelativeTime,
  isoDateInTz,
} from "@/design-system/lib/format";

/**
 * Read the current user's IANA timezone, falling back to Phoenix.
 *
 * Reads `AuthContext` directly (from its own module) rather than going
 * through `useAuth` / `useOptionalAuth` so the dozens of tests that
 * `vi.mock("@/hooks/useAuth")` and replace the entire module surface
 * don't have to also stub a `useOptionalAuth` export. When no provider
 * wraps the render — production logged-out flows or test fixtures —
 * the context is `undefined` and the helper falls back to Phoenix.
 */
export function useUserTimeZone(): string {
  const ctx = useContext(AuthContext);
  return ctx?.user?.timeZone || FALLBACK_TIMEZONE;
}

/**
 * React-component-friendly formatter. Resolves the zone once and returns a
 * memoised bundle of the pure helpers pre-bound to it. The `tz` field is
 * also returned so consumers can pass it on to pure code (e.g. table-column
 * factories) without re-reading the context.
 *
 * 🔴 IT READS `AuthContext` DIRECTLY, NOT `useAuth`, AND THE DIFFERENCE
 * MATTERS IN TESTS. This comment used to say `useAuth`, which sends a test
 * author to `vi.mock("@/hooks/useAuth")` — a mock that supplies no zone at
 * all, so the component silently falls back to Phoenix and an assertion
 * about the viewer's zone passes for the wrong reason. Mount a real
 * `<AuthContext.Provider>` (note `renderWithProviders` supplies none), or
 * pass `tz` explicitly.
 */
export function useDateFormatter(): {
  dateTime: (iso: string | Date | null | undefined, opts?: Intl.DateTimeFormatOptions) => string;
  date: (iso: string | Date | null | undefined, opts?: Intl.DateTimeFormatOptions) => string;
  time: (iso: string | Date | null | undefined, opts?: Intl.DateTimeFormatOptions) => string;
  isoDate: (iso: string | Date | null | undefined) => string;
  relative: (iso: string | Date | null | undefined) => string;
  tz: string;
} {
  const tz = useUserTimeZone();
  return useMemo(
    () => ({
      dateTime: (iso, opts) => formatDateTimeInTz(iso, tz, opts),
      date: (iso, opts) => formatDateInTz(iso, tz, opts),
      time: (iso, opts) => formatTimeInTz(iso, tz, opts),
      isoDate: (iso) => isoDateInTz(iso, tz),
      relative: (iso) => formatRelativeTime(iso, tz),
      tz,
    }),
    [tz],
  );
}
